import {
  Box,
  Flex,
  InputGroup,
  InputRightAddon,
  NumberDecrementStepper,
  NumberIncrementStepper,
  NumberInput,
  NumberInputField,
  NumberInputProps,
  NumberInputStepper,
} from '@chakra-ui/react'
import { Field } from '@hb/shared'
import { CSSProperties, forwardRef } from 'react'
import { FieldInputProps, FieldRenderProps } from 'react-final-form'
import { useStatusBorderColor } from '../Shared/hooks'
import { getInputContainerPaddingX, getInputContainerPaddingY } from '../Shared/InputContainer'
import { useInputSize } from '../Shared/sizes'
import { baseInputStyle } from '../Shared/styles'

export interface BaseNumberInputProps {
  input: FieldInputProps
  onChange: NumberInputProps['onChange']
  meta: FieldRenderProps['meta']
  disabled: boolean | undefined
  placeholder?: string
  field: Field
  readOnly: boolean | undefined
  style?: CSSProperties
  inputStyle?: CSSProperties
  value: string
  min?: number
  max?: number
  optional?: boolean
  defaultValue?: number
  step?: number
  precision?: number
  unit?: string
  withStepper?: boolean
}

export const BaseNumberInput = forwardRef<HTMLInputElement, BaseNumberInputProps>(
  (
    {
      field,
      meta,
      input,
      onChange,
      disabled,
      readOnly,
      value,
      style,
      inputStyle,
      min,
      max,
      optional,
      defaultValue,
      step,
      precision,
      placeholder,
      unit,
      withStepper,
    },
    ref,
  ) => {
    const borderColor = useStatusBorderColor(field, meta)

    const size = useInputSize(field?.size)
    // When the field isn't a row item it otherwise shrinks to fit its (often empty) value and
    // clips the placeholder. Give it an explicit width that's at least 120px and wide enough for
    // the placeholder text — ~1ch per character (approximation for a proportional font) plus the
    // Box (inputContainerPaddingX) + input (baseInputStyle) horizontal padding.
    const collapsedWidth = `max(50px, calc(${placeholder?.length ?? 0}ch))`
    const fieldWidth = field.row ? '100%' : collapsedWidth

    const defaultInputStyle = baseInputStyle(size, disabled)
    return (
      <Flex
        w={field.row ? '100%' : undefined}
        borderRadius="6px"
        data-child-name={input.name}
        minH={0}>
        <InputGroup
          height="auto"
          minH={0}
          border="0.5px solid"
          borderColor={borderColor}
          transition="border-color 0.15s ease"
          borderRadius="8px"
          width={field.row ? '100%' : 'auto'}
          bg="white"
          alignItems="center"
          // style={style}
        >
          <NumberInput
            {...input}
            isDisabled={disabled}
            isReadOnly={readOnly}
            onChange={onChange}
            value={value}
            // size="md"
            lineHeight={1}
            onFocus={e => {
              input.onFocus(e)
              e.target.select()
            }}
            onMouseUp={e => {
              e.preventDefault()
              // e.currentTarget.focus()
            }}
            min={min}
            max={max}
            isRequired={!optional}
            h="auto"
            width={field.row ? '100%' : 'auto'}
            flex={field.row ? 1 : undefined}
            // width="100%"
            defaultValue={defaultValue}
            style={{ minHeight: 0 }}
            step={step}
            precision={precision ?? 0}>
            <Box
              flex={field.row ? 1 : undefined}
              px={getInputContainerPaddingX(size)}
              data-child-name={input.name}
              w={field.row ? '100%' : undefined}
              py={getInputContainerPaddingY(size)}
              style={style}>
              <NumberInputField
                ref={ref}
                _focus={{
                  boxShadow: 'none',
                }}
                _placeholder={{ color: '#aaaaaa', fontSize: defaultInputStyle.fontSize }}
                outline="none"
                border="none"
                placeholder={placeholder}
                w={fieldWidth}
                minH="0"
                h="auto"
                borderColor="#cdcdcd"
                // baseInputStyle sets a tight lineHeight (1.1rem); with h="auto" the field height
                // collapses to that line box and clips descenders/subscripts in placeholders
                // (e.g. "pCO₂", "pO₂"). Restore a normal line-height so the full glyph is visible.
                style={{ ...defaultInputStyle, lineHeight: 'normal', ...inputStyle }}
              />
            </Box>
            {withStepper ? (
              <NumberInputStepper>
                <NumberIncrementStepper
                  borderColor="#cdcdcd"
                  opacity={0.5}
                  _hover={{ opacity: 0.8 }}
                />
                <NumberDecrementStepper
                  borderColor="#cdcdcd"
                  opacity={0.5}
                  _hover={{ opacity: 0.8 }}
                />
              </NumberInputStepper>
            ) : null}
          </NumberInput>
          {unit ? (
            <InputRightAddon
              py={`calc(${getInputContainerPaddingY(size)} - 0.125rem)`}
              px={getInputContainerPaddingX(size)}
              height="100%"
              borderRightWidth={0}
              borderTopWidth={0}
              fontSize={defaultInputStyle.fontSize}
              borderBottomWidth={0}
              borderLeft="1px solid #cdcdcd">
              {unit}
            </InputRightAddon>
          ) : null}
        </InputGroup>
      </Flex>
    )
  },
)
